import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Users, Mail, Phone, Award, Clock, Edit, Trash2, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import TeamMemberDetail from './TeamMemberDetail';

const OpsTeam = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    role: 'cleaner', 
    hourly_rate: '', 
    max_hours_per_week: 40, 
    skills: [], 
    status: 'active' 
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      // Fetch both ops team members and employees
      const [opsTeamRes, employeesRes] = await Promise.all([
        api.get('/ops/team'),
        api.get('/employees')
      ]);
      
      const opsTeam = opsTeamRes.data || [];
      const employees = employeesRes.data || [];
      
      // Convert employees to ops team format and merge
      const employeesAsTeam = employees
        .filter(emp => emp.status === 'active')
        .map(emp => ({
          id: emp.id,
          name: emp.name,
          email: emp.email,
          phone: emp.phone,
          role: emp.employee_type || 'cleaner',
          hourly_rate: emp.hourly_rate,
          max_hours_per_week: 40,
          skills: [],
          status: emp.status,
          source: 'employee' // Track source
        }));
      
      // Merge ops team and employees (deduplicate by ID)
      const allMembers = [...opsTeam, ...employeesAsTeam];
      const uniqueMembers = allMembers.reduce((acc, member) => {
        if (!acc.find(m => m.id === member.id)) {
          acc.push(member);
        }
        return acc;
      }, []);
      
      setMembers(uniqueMembers);
    } catch (error) {
      console.error('Error fetching team:', error);
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        email: member.email || '',
        phone: member.phone || '',
        role: member.role,
        hourly_rate: member.hourly_rate?.toString() || '',
        max_hours_per_week: member.max_hours_per_week || 40,
        skills: member.skills || [],
        status: member.status
      });
    } else {
      setEditingMember(null);
      setFormData({ 
        name: '', 
        email: '', 
        phone: '', 
        role: 'cleaner', 
        hourly_rate: '', 
        max_hours_per_week: 40, 
        skills: [], 
        status: 'active' 
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.role) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      if (editingMember) {
        // Check if this is an employee (synced from employees collection)
        if (editingMember.source === 'employee') {
          // For employees, we need to update via the employees endpoint
          // Map ops team fields back to employee fields
          const employeePayload = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            employee_type: formData.role, // role -> employee_type
            hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : 0,
            status: formData.status,
            // Note: Skills are not part of Employee model, so they won't be saved for synced employees
          };
          await api.put(`/employees/${editingMember.id}`, employeePayload);
          toast.success('Employee updated successfully');
        } else {
          // For ops team members, update via ops/team endpoint
          const payload = { 
            ...formData, 
            hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null 
          };
          await api.put(`/ops/team/${editingMember.id}`, payload);
          toast.success('Team member updated');
        }
      } else {
        // Create new ops team member
        const payload = { 
          ...formData, 
          hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null 
        };
        await api.post('/ops/team', payload);
        toast.success('Team member added');
      }
      
      setDialogOpen(false);
      setEditingMember(null);
      setFormData({ name: '', email: '', phone: '', role: 'cleaner', hourly_rate: '', max_hours_per_week: 40, skills: [], status: 'active' });
      fetchMembers();
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error('Failed to save team member');
    }
  };

  const handleDeleteClick = (member, e) => {
    e.stopPropagation();
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!memberToDelete) return;
    
    try {
      await api.delete(`/ops/team/${memberToDelete.id}`);
      toast.success('Team member deleted');
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      fetchMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Failed to delete team member');
    }
  };

  const handleEditClick = (member, e) => {
    e.stopPropagation();
    handleOpenDialog(member);
  };

  const handleMemberClick = (member) => {
    setSelectedMember(member);
  };

  // If a member is selected, show detail view
  if (selectedMember) {
    return <TeamMemberDetail member={selectedMember} onBack={() => setSelectedMember(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Team Management</h2>
          <p className="text-slate-600 text-sm mt-1">Manage team members, skills, and availability</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-[#247C75] hover:bg-[#1d6259]">
          <Plus className="h-4 w-4 mr-2" />New Member
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map(member => (
          <Card 
            key={member.id} 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleMemberClick(member)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg">{member.name}</CardTitle>
                  <Badge className="mt-1 capitalize">{member.role}</Badge>
                  {member.source === 'employee' && (
                    <Badge variant="outline" className="mt-1 ml-2 text-xs">From Employees</Badge>
                  )}
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <Badge 
                    variant={member.status === 'active' ? 'default' : 'secondary'}
                    className={member.status === 'active' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-slate-100 text-slate-700'}
                  >
                    {member.status === 'active' ? 'Available' : 'Unavailable'}
                  </Badge>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-blue-50"
                      onClick={(e) => handleEditClick(member, e)}
                    >
                      <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-red-50"
                      onClick={(e) => handleDeleteClick(member, e)}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {member.email && <div className="flex items-center gap-2 text-sm text-slate-600"><Mail className="h-4 w-4" />{member.email}</div>}
              {member.phone && <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="h-4 w-4" />{member.phone}</div>}
              {member.skills?.length > 0 && (
                <div className="flex items-start gap-2 text-sm">
                  <Award className="h-4 w-4 text-[#247C75] mt-0.5" />
                  <div className="flex flex-wrap gap-1">
                    {member.skills.map((skill, idx) => <Badge key={idx} variant="outline" className="text-xs">{skill}</Badge>)}
                  </div>
                </div>
              )}
              <div className="text-xs text-slate-500 pt-2 border-t">
                ${member.hourly_rate || 0}/hr • {member.max_hours_per_week || 40}h/week max
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cleaner">Cleaner</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hourly Rate</Label>
                <Input type="number" step="0.01" value={formData.hourly_rate} onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Max Hours/Week</Label>
                <Input type="number" value={formData.max_hours_per_week} onChange={(e) => setFormData({...formData, max_hours_per_week: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Skills & Expertise */}
            {editingMember?.source !== 'employee' && (
              <div className="space-y-2">
                <Label>Skills & Expertise</Label>
                <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px]">
                  {formData.skills.map((skill, idx) => (
                    <Badge 
                      key={idx} 
                      variant="outline" 
                      className="bg-[#247C75]/10 text-[#247C75] border-[#247C75]/30 flex items-center gap-1"
                    >
                      {skill}
                      <button
                        onClick={() => {
                          const newSkills = formData.skills.filter((_, i) => i !== idx);
                          setFormData({...formData, skills: newSkills});
                        }}
                        className="ml-1 hover:text-red-600"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add skill (e.g., residential, commercial, deep_clean)"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value && !formData.skills.includes(value)) {
                          setFormData({...formData, skills: [...formData.skills, value]});
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => {
                      const input = e.target.closest('div').querySelector('input');
                      const value = input.value.trim();
                      if (value && !formData.skills.includes(value)) {
                        setFormData({...formData, skills: [...formData.skills, value]});
                        input.value = '';
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Press Enter or click Add to add a skill</p>
              </div>
            )}
            {editingMember?.source === 'employee' && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Skills cannot be edited for employees synced from Project Tracker → Employees. 
                  Other fields will be updated in the Employees page.
                </p>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit} className="bg-[#247C75] hover:bg-[#1d6259]">
                {editingMember ? 'Update Member' : 'Add Member'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {memberToDelete?.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpsTeam;